import { useEffect } from 'preact/hooks';
import { engine } from '../engine/engine.js';
import { useEngineState } from './engine-state.js';
import { Home, SongSheet } from './home.js';
import { useKeys } from './keys.js';
import { saveSong } from './library.js';
import { Pickers } from './pickers.js';
import { Player } from './player.js';
import { CuesSheet, LoopSheet, SpeedSheet, TestMeSheet } from './practice-sheets.js';
import { DiagnosticsSheet } from './diagnostics.js';
import { AboutSheet, CalibrateSheet, HelpSheet, HowSheet, MenuSheet, TruthSheet } from './sheets.js';
import { LiveRegion } from './toast.js';
import { SONG_END_TEXT } from './sponsor.js';
import { announce, hideCountNote, inBigMode, openSheet, showCountNote, ui } from './ui.js';
import { ReviewSheet } from './taps.js';
import { autoLine } from './actions.js';

function SheetHost() {
  const { sheet } = ui.use();
  switch (sheet) {
    case 'menu': return <MenuSheet />;
    case 'loop': return <LoopSheet />;
    case 'speed': return <SpeedSheet />;
    case 'cues': return <CuesSheet />;
    case 'testme': return <TestMeSheet />;
    case 'calibrate': return <CalibrateSheet />;
    case 'truth': return <TruthSheet />;
    case 'diagnostics': return <DiagnosticsSheet />;
    case 'about': return <AboutSheet />;
    case 'help': return <HelpSheet />;
    case 'song': return <SongSheet />;
    case 'review': return <ReviewSheet />;
    case 'howto': return <HowSheet />;
    default: return null;
  }
}

/** Keep "Your songs" up to date: a song is written when its taps change (it is "Counted" at two). */
function useLibraryWriter(): void {
  useEffect(() => {
    let last = '';
    return engine.subscribe((s) => {
      if (s.source !== 'file' || !s.track || s.status === 'decoding' || s.status === 'error') return;
      const counted = s.taps >= 2 || s.auto.origin !== 'none';
      const key = `${s.track.id}:${counted}`;
      if (key === last) return;
      last = key;
      void saveSong({ id: s.track.id, name: s.track.name, duration: s.track.duration, taps: s.taps, counted, openedAt: Date.now() }).catch(() => { /* storage refused: no list */ });
    });
  }, []);
}

/** State changes go to the polite live region: "Counting from your taps". */
function useAnnouncer(): void {
  const s = useEngineState();
  useEffect(() => {
    if (s.source === 'lab') announce('Rhythm Lab. The count is running.');
  }, [s.source === 'lab', s.lab?.bpm]);
  useEffect(() => {
    if (s.source !== 'file') return;
    if (s.status === 'needs-taps') { if (!(s.auto.run === 'finding' && s.taps === 0)) announce('Tap when you feel 1.'); }
    else if (s.status === 'provisional') { if (s.auto.origin === 'taps') announce('Counting from your taps.'); }
    else if (s.status === 'locked') announce('Count locked.');
    else if (s.status === 'error' && s.error) announce(s.error);
  }, [s.status, s.track?.id]);
  // The automatic count (S9e): "Finding the count…", then the proposal with its question, or the usual prompt when the model declined.
  useEffect(() => {
    if (s.source !== 'file') return;
    if (s.auto.run === 'finding' && s.status === 'needs-taps' && s.taps === 0) announce('Finding the count…');
    else if (s.auto.run === 'declined' && s.status === 'needs-taps' && s.taps === 0) announce('Tap when you feel 1.');
  }, [s.auto.run, s.track?.id, s.status === 'needs-taps']);
  const asked = s.auto.awaiting && s.status === 'provisional' ? s.auto.oneSure : null;
  useEffect(() => {
    if (asked !== null) announce(`${autoLine(asked)} Yes, 1↔5, ½ beat, Too fast and Too slow are below.`);
  }, [asked, s.track?.id]);
  // Failures are said aloud too, and again if the message changes.
  useEffect(() => { if (s.saveError) announce(s.saveError); }, [s.saveError]);
  useEffect(() => { if (s.trouble) announce(s.trouble.message); }, [s.trouble?.message]);
}

/**
 * "Count set from 2 taps · Replay", once per song: when the dancer's own taps first give the song a count. A song that already has its count when it
 * opens is never told it (it is not seen without a count first), and neither is a song told before (ui.ts keeps that on this device).
 */
function useCountNote(): void {
  useEffect(() => {
    let armed: string | null = null; // the song seen without a count since it opened
    return engine.subscribe((s) => {
      if (s.source !== 'file' || !s.track || s.truthMode) { armed = null; return; }
      if (s.status === 'needs-taps') { armed = s.track.id; hideCountNote(); } // the count is gone (a tap was removed): so is the note
      else if ((s.status === 'provisional' || s.status === 'locked') && armed === s.track.id) {
        armed = null;
        if (s.auto.origin === 'taps') showCountNote(s.track.id, s.taps); // a count the proposal gave is not "set from taps"
      }
    });
  }, []);
}

/**
 * A file played to its end: the stage's status line says "That's the song..." (stage.tsx) and the live region says it too. In Big mode (and in
 * landscape on a phone) nothing is said, and the end is forgotten so that leaving Big mode does not bring the line back (DESIGN §10, rule 1).
 */
function useSongEnd(): void {
  useEffect(() => {
    let was = false;
    return engine.subscribe((s) => {
      if (s.ended === was) return;
      was = s.ended;
      if (!s.ended) return;
      if (ui.get().screen !== 'play' || inBigMode()) engine.clearEnded();
      else announce(SONG_END_TEXT);
    });
  }, []);
}

/** A replay says which tap it is at, and when it ends the Review sheet that started it comes back. */
function useReplayAnnouncer(): void {
  const s = useEngineState();
  useEffect(() => {
    if (s.replay) { hideCountNote(); announce(`Replaying tap ${s.replay.tap} of ${s.replay.of}`); }
  }, [s.replay?.tap, s.replay?.id]);
  const running = s.replay !== null;
  useEffect(() => {
    if (running || !ui.get().reopenReview) return;
    ui.patch({ reopenReview: false });
    if (ui.get().screen === 'play' && ui.get().sheet === null) openSheet('review');
  }, [running]);
}

export function App() {
  const s = useEngineState();
  const { screen } = ui.use();
  useKeys();
  useLibraryWriter();
  useAnnouncer();
  useCountNote();
  useReplayAnnouncer();
  useSongEnd();
  const player = screen === 'play' && s.source !== 'none';
  return (
    <>
      {player ? <Player /> : <Home />}
      <SheetHost />
      <LiveRegion />
      <Pickers />
    </>
  );
}
